import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  CCard, CCardBody, CButton, CFormInput, CInputGroup,
  CTable, CTableHead, CTableRow, CTableHeaderCell, CTableBody, CTableDataCell,
  CSpinner, CAlert, CPagination, CPaginationItem, CRow, CCol, CBadge,
  CModal, CModalHeader, CModalTitle, CModalBody, CModalFooter,
} from '@coreui/react';
import CIcon from '@coreui/icons-react';
import { cilPencil, cilSearch, cilX, cilPlus, cilTask, cilInfo } from '@coreui/icons';
import api from '../services/api';
import SortableHeader from '../components/SortableHeader';

const STATUS_COLORS = {
  'Baru': 'primary', 'Dalam Proses': 'warning', 'Selesai': 'success',
  'Dipulangkan': 'info', 'Menunggu Maklumbalas': 'secondary', 'Waiting to Pickup': 'dark',
};

const PRIORITY_COLORS = { 'Penting': 'danger', 'Sederhana': 'warning', 'Biasa': 'secondary' };

function PaginationBar({ page, totalPages, setPage }) {
  const [goTo, setGoTo] = useState('');
  const pages = [];
  if (totalPages <= 9) {
    for (let i = 1; i <= totalPages; i++) pages.push(i);
  } else {
    [1, 2, 3].forEach(p => pages.push(p));
    if (page > 5) pages.push({ ellipsis: true, key: 'left' });
    for (let i = Math.max(4, page - 1); i <= Math.min(totalPages - 3, page + 1); i++) pages.push(i);
    if (page < totalPages - 4) pages.push({ ellipsis: true, key: 'right' });
    [totalPages - 2, totalPages - 1, totalPages].forEach(p => pages.push(p));
  }
  const handleGoTo = (e) => {
    e.preventDefault();
    const n = parseInt(goTo, 10);
    if (n >= 1 && n <= totalPages) { setPage(n); setGoTo(''); }
  };
  return (
    <div className="d-flex align-items-center gap-3 flex-wrap mt-3">
      <CPagination className="mb-0">
        <CPaginationItem disabled={page === 1} onClick={() => setPage(p => p - 1)}>‹</CPaginationItem>
        {pages.map((p) =>
          p.ellipsis
            ? <CPaginationItem key={p.key} disabled>…</CPaginationItem>
            : <CPaginationItem key={p} active={p === page} onClick={() => setPage(p)}>{p}</CPaginationItem>
        )}
        <CPaginationItem disabled={page === totalPages} onClick={() => setPage(p => p + 1)}>›</CPaginationItem>
      </CPagination>
      <div className="d-flex align-items-center gap-2">
        <small className="text-muted">Page {page} of {totalPages}</small>
        <span className="text-muted">·</span>
        <form onSubmit={handleGoTo} className="d-flex align-items-center gap-1">
          <span className="text-muted small">Go to</span>
          <input type="number" min={1} max={totalPages} value={goTo} onChange={e => setGoTo(e.target.value)}
            placeholder="#" style={{ width: 56 }} className="form-control form-control-sm text-center" />
          <button type="submit" className="btn btn-sm btn-outline-secondary px-2">→</button>
        </form>
      </div>
    </div>
  );
}

function JobPreviewModal({ job, onClose, onEdit }) {
  if (!job) return null;
  const alat = Array.isArray(job.alat) ? job.alat : (() => { try { return JSON.parse(job.alat || '[]'); } catch { return []; } })();
  const perhubungan = Array.isArray(job.perhubungan) ? job.perhubungan : (() => { try { return JSON.parse(job.perhubungan || '[]'); } catch { return []; } })();
  return (
    <CModal visible onClose={onClose} alignment="center" size="lg">
      <CModalHeader>
        <CModalTitle>
          <CIcon icon={cilTask} className="me-2 text-success" />
          {job.jobcode}
          {job.customer_name && <span className="ms-2 text-muted fw-normal" style={{ fontSize: 15 }}>— {job.customer_name}</span>}
        </CModalTitle>
      </CModalHeader>
      <CModalBody>
        <CRow className="g-3 small">
          <CCol xs={6} md={4}>
            <div className="text-muted text-uppercase" style={{ fontSize: 10 }}>Status</div>
            <CBadge color={STATUS_COLORS[job.status] || 'secondary'}>{job.status}</CBadge>
          </CCol>
          <CCol xs={6} md={4}>
            <div className="text-muted text-uppercase" style={{ fontSize: 10 }}>Priority</div>
            <CBadge color={PRIORITY_COLORS[job.keutamaan] || 'secondary'}>{job.keutamaan || '—'}</CBadge>
          </CCol>
          <CCol xs={6} md={4}>
            <div className="text-muted text-uppercase" style={{ fontSize: 10 }}>Date</div>
            <div className="fw-medium">{job.date || '—'}</div>
          </CCol>
          <CCol xs={6} md={4}>
            <div className="text-muted text-uppercase" style={{ fontSize: 10 }}>Staff</div>
            <div className="fw-medium">{job.admin_id || '—'}</div>
          </CCol>
          <CCol xs={6} md={4}>
            <div className="text-muted text-uppercase" style={{ fontSize: 10 }}>Customer ID</div>
            <div className="fw-medium">{job.custid || '—'}</div>
          </CCol>
          <CCol xs={6} md={4}>
            <div className="text-muted text-uppercase" style={{ fontSize: 10 }}>Cost (RM)</div>
            <div className="fw-medium">{job.kos || '—'}</div>
          </CCol>
          <CCol xs={12} md={8}>
            <div className="text-muted text-uppercase" style={{ fontSize: 10 }}>Equipment / Description</div>
            <div className="fw-medium">{job.jenis || '—'}</div>
          </CCol>
          {alat.length > 0 && (
            <CCol xs={12} md={8}>
              <div className="text-muted text-uppercase" style={{ fontSize: 10 }}>Devices</div>
              <div className="d-flex flex-wrap gap-1 mt-1">
                {alat.map(a => <CBadge key={a} color="light" textColor="dark">{a}</CBadge>)}
              </div>
            </CCol>
          )}
          {job.peralatan_lain && (
            <CCol xs={12} md={4}>
              <div className="text-muted text-uppercase" style={{ fontSize: 10 }}>Other Equipment</div>
              <div className="fw-medium">{job.peralatan_lain}</div>
            </CCol>
          )}
          {perhubungan.length > 0 && (
            <CCol xs={12}>
              <div className="text-muted text-uppercase" style={{ fontSize: 10 }}>Contact Methods</div>
              <div className="d-flex flex-wrap gap-1 mt-1">
                {perhubungan.map(p => <CBadge key={p} color="info" textColor="dark">{p}</CBadge>)}
              </div>
            </CCol>
          )}
          {job.catatan && (
            <CCol xs={12}>
              <div className="text-muted text-uppercase" style={{ fontSize: 10 }}>Notes</div>
              <div className="fw-medium" style={{ whiteSpace: 'pre-wrap' }}>{job.catatan}</div>
            </CCol>
          )}
        </CRow>
      </CModalBody>
      <CModalFooter>
        <CButton color="success" size="sm" onClick={() => onEdit(job.jobcode)}>
          <CIcon icon={cilPencil} className="me-1" /> Edit
        </CButton>
        <CButton color="secondary" variant="outline" onClick={onClose}>Close</CButton>
      </CModalFooter>
    </CModal>
  );
}

export default function JobsPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const qParams = new URLSearchParams(location.search);
  const qStatus      = qParams.get('status')      || '';
  const qAdminId     = qParams.get('admin_id')    || '';
  const qDatePattern = qParams.get('datePattern') || '';
  const qLabel       = qParams.get('label')       || '';
  const qCustid      = qParams.get('custid')      || '';

  const [jobs, setJobs] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [statusFilter, setStatusFilter] = useState(qStatus);
  const [adminFilter, setAdminFilter] = useState(qAdminId);
  const [datePattern, setDatePattern] = useState(qDatePattern);
  const [filterLabel, setFilterLabel] = useState(qLabel);
  const [custidFilter, setCustidFilter] = useState(qCustid);
  const [sortBy, setSortBy] = useState('date');
  const [sortDir, setSortDir] = useState('desc');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [previewJob, setPreviewJob] = useState(null);

  const pageSize = 20;
  const totalPages = Math.ceil(total / pageSize);

  function handleSort(col) {
    if (col === sortBy) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortBy(col); setSortDir('asc'); }
    setPage(1);
  }

  useEffect(() => {
    setStatusFilter(qStatus);
    setAdminFilter(qAdminId);
    setDatePattern(qDatePattern);
    setFilterLabel(qLabel);
    setCustidFilter(qCustid);
    setPage(1);
  }, [qStatus, qAdminId, qDatePattern, qLabel, qCustid]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchJobs = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await api.get('/jobs', { params: { page, search, status: statusFilter, sortBy, sortDir, admin_id: adminFilter, datePattern, custid: custidFilter } });
      setJobs(data.jobs);
      setTotal(data.total);
    } catch {
      setError('Failed to load jobs.');
    } finally {
      setLoading(false);
    }
  }, [page, search, statusFilter, sortBy, sortDir, adminFilter, datePattern, custidFilter]);

  useEffect(() => { fetchJobs(); }, [fetchJobs]);

  function handleSearch(e) {
    e.preventDefault();
    setPage(1);
    setSearch(searchInput);
  }

  return (
    <>
      <JobPreviewModal
        job={previewJob}
        onClose={() => setPreviewJob(null)}
        onEdit={(jobcode) => { setPreviewJob(null); navigate(`/jobs/${jobcode}/edit`); }}
      />

      <CRow className="mb-3 align-items-center">
        <CCol>
          <h5 className="mb-0">Jobs</h5>
        </CCol>
        <CCol xs="auto">
          <CButton color="success" size="sm" onClick={() => navigate('/jobs/new')}>
            <CIcon icon={cilPlus} className="me-1" /> New Job
          </CButton>
        </CCol>
      </CRow>

      <CCard>
        <CCardBody>
          <form onSubmit={handleSearch} className="d-flex gap-2 mb-3">
            <CInputGroup>
              <CFormInput
                placeholder="Search by job code…"
                value={searchInput}
                onChange={e => setSearchInput(e.target.value)}
              />
              <CButton type="submit" color="secondary">
                <CIcon icon={cilSearch} />
              </CButton>
              {search && (
                <CButton color="secondary" variant="outline" onClick={() => { setSearch(''); setSearchInput(''); setPage(1); }}>
                  <CIcon icon={cilX} />
                </CButton>
              )}
            </CInputGroup>
            {statusFilter && (
              <CButton color="secondary" variant="outline" size="sm" onClick={() => { setStatusFilter(''); setPage(1); }}>
                <CIcon icon={cilX} className="me-1" />{statusFilter}
              </CButton>
            )}
            {(adminFilter || datePattern) && (
              <CButton color="info" variant="outline" size="sm" onClick={() => { setAdminFilter(''); setDatePattern(''); setFilterLabel(''); setPage(1); }}>
                <CIcon icon={cilX} className="me-1" />{filterLabel || `${adminFilter}${datePattern ? ` · ${datePattern.replace(/%|\//g, m => m === '%' ? '' : '/')}` : ''}`}
              </CButton>
            )}
            {custidFilter && (
              <CButton color="success" variant="outline" size="sm" onClick={() => { setCustidFilter(''); setPage(1); }}>
                <CIcon icon={cilX} className="me-1" />Customer: {custidFilter}
              </CButton>
            )}
          </form>

          {error && <CAlert color="danger">{error}</CAlert>}

          {loading ? (
            <div className="text-center py-4"><CSpinner color="success" /></div>
          ) : (
            <>
              <div className="mb-2">
                <small className="text-muted d-flex align-items-center gap-1">
                  <CIcon icon={cilTask} size="sm" className="text-success" />
                  {search || statusFilter
                    ? <><strong>{total}</strong> result{total !== 1 ? 's' : ''}</>
                    : <><strong>{total}</strong> job{total !== 1 ? 's' : ''}</>}
                </small>
              </div>

              <CTable hover responsive className="mb-0">
                <CTableHead color="light">
                  <CTableRow>
                    <SortableHeader col="jobcode"   label="Job Code"  sortBy={sortBy} sortDir={sortDir} onSort={handleSort} />
                    <CTableHeaderCell>Customer</CTableHeaderCell>
                    <SortableHeader col="status"    label="Status"    sortBy={sortBy} sortDir={sortDir} onSort={handleSort} />
                    <SortableHeader col="keutamaan" label="Priority"  sortBy={sortBy} sortDir={sortDir} onSort={handleSort} />
                    <SortableHeader col="admin_id"  label="Staff"     sortBy={sortBy} sortDir={sortDir} onSort={handleSort} />
                    <SortableHeader col="date"      label="Date"      sortBy={sortBy} sortDir={sortDir} onSort={handleSort} />
                    <CTableHeaderCell style={{ width: 80 }}></CTableHeaderCell>
                  </CTableRow>
                </CTableHead>
                <CTableBody>
                  {jobs.map(j => (
                    <CTableRow key={j.jid} style={{ cursor: 'pointer' }} onClick={() => navigate(`/jobs/${j.jobcode}/edit`)}>
                      <CTableDataCell>{j.jobcode}</CTableDataCell>
                      <CTableDataCell>
                        <div className="d-flex align-items-center gap-1">
                          <span>{j.customer_name || <span className="text-muted">—</span>}</span>
                          <CButton
                            size="sm" color="secondary" variant="ghost"
                            style={{ padding: '0 4px', lineHeight: 1 }}
                            onClick={e => { e.stopPropagation(); setPreviewJob(j); }}
                            title="Preview job"
                          >
                            <CIcon icon={cilInfo} size="sm" />
                          </CButton>
                        </div>
                      </CTableDataCell>
                      <CTableDataCell>
                        <CBadge color={STATUS_COLORS[j.status] || 'secondary'}>{j.status}</CBadge>
                      </CTableDataCell>
                      <CTableDataCell>{j.keutamaan || <span className="text-muted">—</span>}</CTableDataCell>
                      <CTableDataCell>{j.admin_id}</CTableDataCell>
                      <CTableDataCell>{j.date}</CTableDataCell>
                      <CTableDataCell onClick={e => e.stopPropagation()}>
                        <CButton size="sm" color="success" variant="ghost" onClick={() => navigate(`/jobs/${j.jobcode}/edit`)} title="Edit">
                          <CIcon icon={cilPencil} />
                        </CButton>
                      </CTableDataCell>
                    </CTableRow>
                  ))}
                  {jobs.length === 0 && (
                    <CTableRow>
                      <CTableDataCell colSpan={7} className="text-center text-muted py-4">No jobs found.</CTableDataCell>
                    </CTableRow>
                  )}
                </CTableBody>
              </CTable>

              {totalPages > 1 && <PaginationBar page={page} totalPages={totalPages} setPage={setPage} />}
            </>
          )}
        </CCardBody>
      </CCard>
    </>
  );
}
